"use client";

import { c as _c } from "react/compiler-runtime";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getTimeAgoShort } from "@/helpers/Helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import DropCurationButton from "../DropCurationButton";
import DropMinimalIdentityRow from "../DropMinimalIdentityRow";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropReactions from "../WaveDropReactions";
import ParticipationIdentityProfileCard from "./ParticipationIdentityProfileCard";
import { getParticipationIdentityProfile, getParticipationVisibleMetadata } from "./participationIdentityProfile.helpers";
import type { DropContentPresentation } from "../dropContentPresentation";
import type { DropIdentityMode, DropInteractionParams, DropTimestampLayout } from "../drop.types";
import { DropLocation, hasDropFooter } from "../drop.types";
interface EndedParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly footer?: React.ReactNode;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
export default function EndedParticipationDrop(t0) {
  const $ = _c(92);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick,
    footer,
    identityMode: t1,
    timestampLayout: t2,
    showInteractions: t3,
    contentPresentation: t4,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const identityMode = t1 === undefined ? "default" : t1;
  const timestampLayout = t2 === undefined ? "inline" : t2;
  const showInteractions = t3 === undefined ? true : t3;
  const contentPresentation = t4 === undefined ? "default" : t4;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const router = useRouter();
  let identityProfile;
  let t5;
  let visibleMetadata;
  if ($[0] !== drop.author || $[1] !== drop.metadata || $[2] !== drop.wave) {
    identityProfile = getParticipationIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    let t6;
    if ($[6] !== drop.metadata || $[7] !== drop.wave) {
      t6 = getParticipationVisibleMetadata({
        wave: drop.wave,
        metadata: drop.metadata
      });
      $[6] = drop.metadata;
      $[7] = drop.wave;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    visibleMetadata = t6;
    t5 = identityProfile ? areSameProfileIdentity({
      left: drop.author,
      right: identityProfile
    }) : false;
    $[0] = drop.author;
    $[1] = drop.metadata;
    $[2] = drop.wave;
    $[3] = identityProfile;
    $[4] = t5;
    $[5] = visibleMetadata;
  } else {
    identityProfile = $[3];
    t5 = $[4];
    visibleMetadata = $[5];
  }
  const isSelfNominee = t5;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  const showIdentity = identityMode !== "hidden";
  const isStackedTimestamp = timestampLayout === "stacked";
  let t6;
  if ($[9] !== router) {
    t6 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[9] = router;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleNavigation = t6;
  let t7;
  if ($[11] !== hasTouch || $[12] !== showInteractions) {
    t7 = () => {
      if (!showInteractions || !hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[11] = hasTouch;
    $[12] = showInteractions;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleLongPress = t7;
  let t8;
  if ($[14] !== activePartIndex || $[15] !== drop || $[16] !== onReply) {
    t8 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[14] = activePartIndex;
    $[15] = drop;
    $[16] = onReply;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleOnReply = t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsSlideUp(false);
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleOnAddReaction = t9;
  const getDropLocationBackground = _temp;
  const dropBackgroundClass = isActiveDrop ? "tw-bg-[#3CCB7F]/10" : getDropLocationBackground();
  const t10 = `${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""} tw-w-full`;
  const t11 = `tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-px-4 tw-py-3 tw-transition-colors tw-duration-200 tw-ease-linear ${dropBackgroundClass}`;
  let t12;
  if ($[19] !== activePartIndex || $[20] !== drop || $[21] !== handleOnReply || $[22] !== isMobile || $[23] !== showInteractions || $[24] !== showReplyAndQuote) {
    t12 = !isMobile && showInteractions && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[19] = activePartIndex;
    $[20] = drop;
    $[21] = handleOnReply;
    $[22] = isMobile;
    $[23] = showInteractions;
    $[24] = showReplyAndQuote;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== drop || $[27] !== showIdentity) {
    t13 = showIdentity && <WaveDropAuthorPfp drop={drop} />;
    $[26] = drop;
    $[27] = showIdentity;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== drop || $[30] !== handleNavigation || $[31] !== identityMode || $[32] !== isStackedTimestamp || $[33] !== showIdentity || $[34] !== timestampLayout) {
    t14 = showIdentity && (identityMode === "minimal" ? <DropMinimalIdentityRow drop={drop} timestampLayout={timestampLayout} /> : <div className="tw-flex tw-flex-col tw-gap-y-2"><div className={isStackedTimestamp ? "tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-y-1" : "tw-flex tw-items-center tw-gap-x-2"}><div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} /><p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link onClick={e_0 => handleNavigation(e_0, `/${drop.author.handle ?? drop.author.primary_address}`)} href={`/${drop.author.handle ?? drop.author.primary_address}`} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{drop.author.handle ?? drop.author.primary_address}</Link></p>{!isStackedTimestamp && <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" />}{!isStackedTimestamp && <p className="tw-mb-0 tw-whitespace-nowrap tw-text-xs tw-font-normal tw-leading-none tw-text-iron-500">{getTimeAgoShort(drop.created_at)}</p>}</div>{isStackedTimestamp && <p className="tw-mb-0 tw-whitespace-nowrap tw-text-xs tw-font-normal tw-leading-none tw-text-iron-500">{getTimeAgoShort(drop.created_at)}</p>}</div><div className="tw-flex tw-w-fit tw-items-center tw-whitespace-nowrap tw-rounded-md tw-border tw-border-solid tw-border-iron-500/25 tw-bg-iron-600/10 tw-px-2 tw-py-0.5 tw-font-medium tw-text-iron-500"><span className="tw-text-xs">Participant</span></div></div>);
    $[29] = drop;
    $[30] = handleNavigation;
    $[31] = identityMode;
    $[32] = isStackedTimestamp;
    $[33] = showIdentity;
    $[34] = timestampLayout;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== drop.wave || $[37] !== handleNavigation || $[38] !== identityMode || $[39] !== showWaveInfo) {
    t15 = identityMode === "default" && showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const isDirectMessage = waveMeta?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={e_1 => handleNavigation(e_1, waveHref)} className="tw-leading-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[36] = drop.wave;
    $[37] = handleNavigation;
    $[38] = identityMode;
    $[39] = showWaveInfo;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== activePartIndex || $[42] !== contentPresentation || $[43] !== drop || $[44] !== embedDepth || $[45] !== embedPath || $[46] !== handleLongPress || $[47] !== hasTouch || $[48] !== maxEmbedDepth || $[49] !== onDropContentClick || $[50] !== onQuoteClick || $[51] !== quotePath) {
    t16 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} hasTouch={hasTouch} contentPresentation={contentPresentation} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[41] = activePartIndex;
    $[42] = contentPresentation;
    $[43] = drop;
    $[44] = embedDepth;
    $[45] = embedPath;
    $[46] = handleLongPress;
    $[47] = hasTouch;
    $[48] = maxEmbedDepth;
    $[49] = onDropContentClick;
    $[50] = onQuoteClick;
    $[51] = quotePath;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== t14 || $[54] !== t15 || $[55] !== t16) {
    t17 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-2">{t14}{t15}{t16}</div>;
    $[53] = t14;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== t13 || $[58] !== t17) {
    t18 = <div className="tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t13}{t17}</div>;
    $[57] = t13;
    $[58] = t17;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== drop.id || $[61] !== identityProfile || $[62] !== isSelfNominee || $[63] !== showIdentity) {
    t19 = identityProfile && <div className={showIdentity ? "tw-ml-[3.25rem]" : ""}><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant="chat" showIdentityHeader={!isSelfNominee} /></div>;
    $[60] = drop.id;
    $[61] = identityProfile;
    $[62] = isSelfNominee;
    $[63] = showIdentity;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  let t20;
  if ($[65] !== visibleMetadata) {
    t20 = visibleMetadata.length > 0 && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><WaveDropMetadata metadata={visibleMetadata} /></div>;
    $[65] = visibleMetadata;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== drop || $[68] !== showInteractions) {
    t21 = showInteractions && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={drop.context_profile_context?.curatable ?? false} isCurated={drop.context_profile_context?.curated ?? false} /><WaveDropReactions drop={drop} /></div>;
    $[67] = drop;
    $[68] = showInteractions;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  let t22;
  if ($[70] !== footer || $[71] !== showIdentity) {
    t22 = hasDropFooter(footer) && <div className={`${showIdentity ? "tw-ml-[3.25rem]" : ""} tw-pb-1 tw-pt-2`}>{footer}</div>;
    $[70] = footer;
    $[71] = showIdentity;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== drop || $[74] !== handleOnReply || $[75] !== isSlideUp || $[76] !== longPressTriggered || $[77] !== showInteractions || $[78] !== showReplyAndQuote) {
    t23 = showInteractions && <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[73] = drop;
    $[74] = handleOnReply;
    $[75] = isSlideUp;
    $[76] = longPressTriggered;
    $[77] = showInteractions;
    $[78] = showReplyAndQuote;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  let t24;
  if ($[80] !== t11 || $[81] !== t12 || $[82] !== t18 || $[83] !== t19 || $[84] !== t20 || $[85] !== t21 || $[86] !== t22 || $[87] !== t23) {
    t24 = <div className={t11}>{t12}{t18}{t19}{t20}{t21}{t22}{t23}</div>;
    $[80] = t11;
    $[81] = t12;
    $[82] = t18;
    $[83] = t19;
    $[84] = t20;
    $[85] = t21;
    $[86] = t22;
    $[87] = t23;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  let t25;
  if ($[89] !== t10 || $[90] !== t24) {
    t25 = <div className={t10}>{t24}</div>;
    $[89] = t10;
    $[90] = t24;
    $[91] = t25;
  } else {
    t25 = $[91];
  }
  return t25;
}
function _temp() {
  return "tw-bg-iron-950 tw-ring-1 tw-ring-inset tw-ring-iron-800";
}